import { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  FileText,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  Wallet,
} from 'lucide-react';
import { useUIStore } from '../../store/ui.store';
import { useAuthStore } from '../../store/auth.store';
import MobileNav from './MobileNav';
import { useThemeStore } from '../../store/theme.store';

interface NavItem {
  label: string;
  icon: React.ElementType;
  path: string;
  color: string;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', color: 'text-purple-500' },
  { label: 'Students', icon: Users, path: '/students', color: 'text-electric-500' },
  { label: 'Batches', icon: CalendarDays, path: '/batches', color: 'text-cyan-500' },
  { label: 'Teachers', icon: Users, path: '/teachers', color: 'text-violet-500', adminOnly: true },
  { label: 'Attendance', icon: CalendarDays, path: '/attendance', color: 'text-lime-600' },
  { label: 'Expenses', icon: Wallet, path: '/expenses', color: 'text-rose-500', adminOnly: true },
  { label: 'Revenue', icon: BarChart3, path: '/revenue', color: 'text-purple-500', adminOnly: true },
  { label: 'Documents', icon: FileText, path: '/documents', color: 'text-electric-400' },
  { label: 'Reports', icon: BarChart3, path: '/reports', color: 'text-tangerine-400' },
  { label: 'Settings', icon: Settings, path: '/settings', color: 'text-purple-400', adminOnly: true },
];

const superAdminItems: NavItem[] = [
  { label: 'Institutes', icon: LayoutDashboard, path: '/admin/institutes', color: 'text-orange-500' },
  { label: 'Bulk Import', icon: FileText, path: '/admin/bulk-import', color: 'text-lime-500' },
];

export default function AppLayout() {
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const { user, logout, isImpersonating, exitInstituteView } = useAuthStore();
  const { resolvedTheme, toggleTheme } = useThemeStore();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="flex h-screen bg-light-bg dark:bg-dark-bg overflow-hidden">
      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarOpen ? 260 : 80 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:flex flex-col glass-sidebar h-full z-30"
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-4 py-3">
          <AnimatePresence>
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="flex items-center gap-2.5 min-w-0"
              >
                <img src="/paathshala-logo.png" alt="Paathshaala" className="w-8 h-8 rounded-lg object-contain flex-shrink-0" />
                <div className="min-w-0">
                  <h1 className="text-base font-bold text-gradient-purple leading-tight truncate">Paathshaala</h1>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <button
            onClick={toggleSidebar}
            className="p-1.5 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-500/10 transition-colors flex items-center gap-1.5 flex-shrink-0"
          >
            {!sidebarOpen && (
              <img src="/paathshala-logo.png" alt="P" className="w-6 h-6 rounded object-contain" />
            )}
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-hide">
          {user?.role === 'super_admin' && !isImpersonating() ? (
            // SaaS Super Admin: show ONLY admin nav items
            superAdminItems.map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                    isActive
                      ? 'bg-orange-50 dark:bg-orange-500/10 shadow-sm'
                      : 'hover:bg-light-elevated dark:hover:bg-dark-elevated'
                  }`}
                >
                  <item.icon className={`w-5 h-5 ${isActive ? item.color : 'text-light-text-muted dark:text-dark-text-muted group-hover:text-light-text dark:group-hover:text-dark-text'}`} />
                  <AnimatePresence>
                    {sidebarOpen && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        className={`text-sm font-medium whitespace-nowrap ${
                          isActive ? 'text-orange-600 dark:text-orange-400' : 'text-light-text-secondary dark:text-dark-text-secondary'
                        }`}
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Link>
              );
            })
          ) : (
            // Institute Admin: show ONLY institute nav items
            navItems.map((item) => {
              if (item.adminOnly && user?.role !== 'admin') return null;
              const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                    isActive
                      ? 'bg-purple-50 dark:bg-purple-500/10 shadow-sm'
                      : 'hover:bg-light-elevated dark:hover:bg-dark-elevated'
                  }`}
                >
                  <item.icon className={`w-5 h-5 ${isActive ? item.color : 'text-light-text-muted dark:text-dark-text-muted group-hover:text-light-text dark:group-hover:text-dark-text'}`} />
                  <AnimatePresence>
                    {sidebarOpen && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        className={`text-sm font-medium whitespace-nowrap ${
                          isActive ? 'text-purple-600 dark:text-purple-400' : 'text-light-text-secondary dark:text-dark-text-secondary'
                        }`}
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {isActive && sidebarOpen && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-500"
                    />
                  )}
                </Link>
              );
            })
          )}
        </nav>

        {/* User & Logout */}
        <div className="p-3 border-t border-light-border dark:border-dark-border">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors w-full text-left"
          >
            <LogOut className="w-5 h-5 text-rose-500" />
            <AnimatePresence>
              {sidebarOpen && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="text-sm font-medium text-rose-500 whitespace-nowrap"
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </motion.aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 25 }}
              className="fixed left-0 top-0 bottom-0 w-[280px] glass-sidebar z-[65] lg:hidden"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-light-border dark:border-dark-border">
                <div className="flex items-center gap-2.5 min-w-0">
                  <img src="/paathshala-logo.png" alt="Paathshaala" className="w-8 h-8 rounded-lg object-contain flex-shrink-0" />
                  <div className="min-w-0">
                    <h1 className="text-base font-bold text-gradient-purple truncate">Paathshaala</h1>
                  </div>
                </div>
                <button onClick={() => setMobileMenuOpen(false)} className="p-1.5">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="px-3 py-4 space-y-1">
                {navItems.map((item) => {
                  if (item.adminOnly && user?.role !== 'admin') return null;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                        isActive ? 'bg-purple-50 dark:bg-purple-500/10' : 'hover:bg-light-elevated dark:hover:bg-dark-elevated'
                      }`}
                    >
                      <item.icon className={`w-5 h-5 ${isActive ? item.color : 'text-light-text-muted'}`} />
                      <span className={`text-sm font-medium ${isActive ? 'text-purple-600 dark:text-purple-400' : ''}`}>
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 w-full">
        {/* Topbar - Hidden on mobile */}
        <header className="hidden md:flex glass-nav h-16 items-center justify-between px-4 lg:px-6 z-20">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-light-text dark:text-dark-text">
              {navItems.find((item) => location.pathname === item.path)?.label || 'Paathshaala CRM'}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === 'super_admin' && (
              <a href="/admin/institutes" className="text-xs font-medium bg-purple-500 text-white px-3 py-1.5 rounded-lg hover:bg-purple-600 transition-colors">
                Admin Panel
              </a>
            )}
            {isImpersonating() && (
              <button
                onClick={() => exitInstituteView()}
                className="text-xs font-medium bg-amber-500 text-white px-3 py-1.5 rounded-lg hover:bg-amber-600 transition-colors"
              >
                Exit {user?.instituteName}
              </button>
            )}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="h-10 w-10 rounded-xl bg-light-elevated dark:bg-dark-elevated border border-light-border dark:border-dark-border flex items-center justify-center text-light-text dark:text-dark-text hover:scale-105 transition-transform"
                aria-label="Toggle theme"
              >
                {resolvedTheme === 'dark' ? <Sun className="h-5 w-5 text-amber-400" /> : <Moon className="h-5 w-5 text-slate-800" />}
              </button>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-50 dark:bg-purple-500/10">
                <div className={`w-2 h-2 rounded-full ${user?.role === 'admin' ? 'bg-purple-500' : 'bg-lime-500'}`} />
                <span className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                  {user?.name}
                </span>
                <span className="text-xs text-light-text-muted dark:text-dark-text-muted capitalize">
                  {user?.role}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content - Full width on mobile */}
        <main className="flex-1 overflow-y-auto w-full bg-light-bg dark:bg-dark-bg">
          <div className="px-3 py-3 md:px-6 md:py-6 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:pb-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Outlet />
            </motion.div>
          </div>
        </main>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileNav />
    </div>
  );
}
